import useExamWorkspace from '@/data/hook/exam/useExamWorkspace';
import useExams from '@/data/hook/exam/useExams';
import Exam from '@/models/Exam';
import { buildChangedObject } from '@/utils/buildChangedObject';
import { examFieldsToDTO } from '@/utils/exam/examFieldsToDTO';

import ExamForm, { ExamFormFields } from './ExamForm';

export type ExamFormServiceSuccess = {
  item: Exam;
  action: 'create' | 'update';
};

type ExamFormServiceProps = {
  coursePathId: number;
  courseLineId: number;
  onSuccess?: (response: ExamFormServiceSuccess) => void;
};

export default function ExamFormService({
  coursePathId,
  courseLineId,
  onSuccess,
}: ExamFormServiceProps) {
  const { exam, cache, slug } = useExamWorkspace();
  const coursePath = { id: coursePathId, slug: slug || '' };
  const { createExam, updateExam, isCreating, isUpdating } = useExams({
    coursePath,
  });

  const handleCreate = async (data: ExamFormFields) => {
    const created = await createExam(examFieldsToDTO(data));
    onSuccess?.({ item: created, action: 'create' });
  };

  const handleUpdate = async (data: ExamFormFields) => {
    if (!exam) return;
    const changedFields = buildChangedObject(exam, examFieldsToDTO(data));
    if (Object.keys(changedFields).length === 0) {
      onSuccess?.({ item: exam, action: 'update' });
      return;
    }
    const updated = await updateExam({ examId: exam.id, data: changedFields });
    cache?.merge(updated);
    onSuccess?.({ item: updated, action: 'update' });
  };

  const handleSubmit = (data: ExamFormFields) =>
    exam ? handleUpdate(data) : handleCreate(data);

  return (
    <ExamForm
      key={exam?.id}
      exam={exam}
      courseLineId={courseLineId}
      coursePath={coursePath}
      onSubmit={handleSubmit}
      isLoading={isCreating || isUpdating}
    />
  );
}
